import React, { useState, useEffect } from 'react';
import TransportRouteModal from '../TransportRouteModal';

const TransportTab = ({
  transportCities,
  transportRoutes,
  newCity,
  setNewCity,
  showAddRoute,
  setShowAddRoute,
  editingRoute,
  setEditingRoute,
  seedTransportData,
  addCity,
  deleteCity,
  saveRoute,
  deleteRoute,
  isDarkMode,
  API,
  adminToken
}) => {
  const [showPricingInfo, setShowPricingInfo] = useState(false);
  const [pricingFormula, setPricingFormula] = useState(null);
  
  // Fetch pricing formula from database
  useEffect(() => {
    const fetchPricingFormula = async () => {
      try {
        const res = await fetch(`${API}/admin/buddy/transport/pricing-formula`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPricingFormula(data);
        }
      } catch (err) {
        console.error('Failed to fetch pricing formula:', err);
      }
    };
    if (API && adminToken) fetchPricingFormula();
  }, [API, adminToken]);
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>🚆 Morocco Transport Routes</h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Manage train, bus, and taxi routes between cities
          </p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowPricingInfo(!showPricingInfo)} 
            className={`px-3 py-2 text-sm rounded-lg ${showPricingInfo ? 'bg-blue-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
            title="View Pricing Formula"
          >
            📊 Pricing Info
          </button>
          {transportRoutes.length === 0 && (
            <button onClick={seedTransportData} className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">
              🌱 Seed Top 20 Routes
            </button>
          )}
          <button onClick={() => { setEditingRoute(null); setShowAddRoute(true); }} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700">
            + Add Route
          </button>
        </div>
      </div>

      {/* Pricing Formula Info Box - Data from Database */}
      {showPricingInfo && pricingFormula && (
        <div className={`rounded-xl p-5 border-2 border-blue-400 ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
          <div className="flex justify-between items-start mb-3">
            <div>
              <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>📊 {pricingFormula.title || 'Pricing Formula'}</h4>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>📁 Stored in: transport_settings collection</p>
            </div>
            <button onClick={() => setShowPricingInfo(false)} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          
          <div className="overflow-x-auto">
            <table className={`w-full text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              <thead>
                <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <th className="text-left py-2 pr-4">Transport</th>
                  <th className="text-left py-2 pr-4">Base</th>
                  <th className="text-left py-2 pr-4">Rate/km</th>
                  <th className="text-left py-2">Example</th>
                </tr>
              </thead>
              <tbody>
                {(pricingFormula.transports || []).map((t, idx) => (
                  <tr key={idx} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <td className="py-2 pr-4">{t.icon} {t.type.charAt(0).toUpperCase() + t.type.slice(1)}</td>
                    <td className="py-2 pr-4">{t.base_mad} MAD</td>
                    <td className="py-2 pr-4">{t.rate_min}-{t.rate_max}</td>
                    <td className="py-2 font-medium text-green-600">{t.example_min}-{t.example_max} MAD</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className={`mt-4 pt-3 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
            <p className={`text-xs font-mono ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <strong>Formula:</strong> {pricingFormula.formula_text}
            </p>
          </div>
          
          <div className={`mt-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            <p className="font-medium mb-1">Sample Results:</p>
            <ul className="space-y-1 text-xs">
              {(pricingFormula.sample_results || []).map((s, idx) => (
                <li key={idx}>
                  • <strong>{s.from} → {s.to}</strong> ({s.distance_km}km): 
                  {s.train && ` Train ${s.train}`}
                  {s.bus && `, Bus ${s.bus}`}
                  {s.taxi && `, Taxi ${s.taxi}`} MAD
                </li>
              ))}
            </ul>
          </div>
          
          <p className={`mt-3 text-xs italic ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
            ⚠️ {pricingFormula.disclaimer}
          </p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow text-center`}>
          <p className="text-2xl font-bold text-purple-500">{transportCities.length}</p>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cities</p>
        </div>
        <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow text-center`}>
          <p className="text-2xl font-bold text-blue-500">{transportRoutes.length}</p>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Routes</p>
        </div>
        <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow text-center`}>
          <p className="text-2xl font-bold text-green-500">{transportRoutes.filter(r => r.is_active).length}</p>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Active</p>
        </div>
      </div>

      {/* Cities Section */}
      <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
        <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>📍 Cities</h4>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={newCity}
            onChange={(e) => setNewCity(e.target.value)}
            placeholder="Add new city..."
            className={`flex-1 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            onKeyPress={(e) => e.key === 'Enter' && addCity()}
          />
          <button onClick={addCity} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg">Add</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {transportCities.map(city => (
            <span key={city.id} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
              {city.name}
              <button onClick={() => deleteCity(city.id)} className="ml-1 text-red-400 hover:text-red-600">×</button>
            </span>
          ))}
        </div>
      </div>

      {/* Routes List */}
      <div className="space-y-3">
        {transportRoutes.length === 0 ? (
          <div className={`text-center py-12 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <p className="text-4xl mb-3">🚆</p>
            <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>No routes yet</p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Click &quot;Seed Top 20 Routes&quot; to get started</p>
          </div>
        ) : (
          transportRoutes.map(route => (
            <div key={route.id} className={`rounded-xl p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    📍 {route.from_city} → {route.to_city}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs ${route.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {route.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setEditingRoute(route); setShowAddRoute(true); }} className="p-1 text-blue-500 hover:bg-blue-50 rounded">✏️</button>
                  <button onClick={() => deleteRoute(route.id)} className="p-1 text-red-500 hover:bg-red-50 rounded">🗑</button>
                </div>
              </div>
              
              {/* Transport Options */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {(route.transports || []).map((t, idx) => (
                  <div key={idx} className={`rounded-lg p-3 border ${t.is_best ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20' : isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">
                        {t.type === 'train' ? '🚆' : t.type === 'bus' ? '🚌' : '🚕'}
                      </span>
                      <span className={`font-medium capitalize ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t.type}</span>
                      {t.is_best && <span className="text-yellow-500">⭐</span>}
                    </div>
                    <div className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <p>⏱ {Math.floor(t.time_minutes / 60)}h{t.time_minutes % 60 > 0 ? (t.time_minutes % 60) + 'm' : ''}</p>
                      <p>💰 {t.price_min}–{t.price_max} MAD</p>
                      {t.direct === false && <p className="text-orange-500">⚠️ Not direct</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Route Modal */}
      {showAddRoute && (
        <TransportRouteModal
          route={editingRoute}
          cities={transportCities}
          isDarkMode={isDarkMode}
          onSave={saveRoute}
          onClose={() => { setShowAddRoute(false); setEditingRoute(null); }}
        />
      )}
    </div>
  );
};

export default TransportTab;
